import { Drawer, Empty, Spin } from "antd";
import styles from "./styles.module.scss";
import UserInfo from "components/cards/UserInfo";
import Button from "components/general/Button";
import { IoMdClose } from "react-icons/io";
import Text from "components/general/Text";
import InfoCard from "components/general/InfoCard";
import { useCallback, useEffect, useMemo, useState } from "react";
import SelectionInput from "components/inputs/SelectionInput";
import {
  useGetPreviewUsersInfiniteQuery,
  usePostAccessListMutation,
  useUpdateAccessListMutation,
} from "apis/services/permissions";
import { Form } from "antd";
import {
  useGetDepartmentsQuery,
  useGetLocationsQuery,
  useGetZonesQuery,
} from "apis/services/locations";
import { useGetJobClassificationsQuery } from "apis/services/other";
import { transformToOptions } from "utils/transformToOptions";
import { transformToGroupedOptions } from "utils/transformToGroupedOptions";
import HandleErrors from "utils/handleErrors";
import showSuccessMsg from "utils/showSuccessMsg";
import useLoadMoreOnScroll from "hooks/useLoadMoreOnScroll";

export default function AccessList({
  containerStyle,
  isVisible,
  setIsVisible,
  selectedAccessListUser,
}: {
  containerStyle?: string;
  isVisible: boolean;
  setIsVisible: (value: boolean) => void;
  selectedAccessListUser: any;
}) {
  const [form] = Form.useForm();
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [postAccessList, { isLoading: isPostAccessListLoading }] =
    usePostAccessListMutation();

  // Watch form values
  const jobClassificationValue = Form.useWatch("job_classification", form);
  const departmentValue = Form.useWatch("department", form);
  const zoneValue = Form.useWatch("zone", form);
  const locationValue = Form.useWatch("location", form);

  // access_list returns arrays of objects [{id, name}] — extract IDs so multiselect can match option values
  const formInitialValues = useMemo(() => {
    const access_list = selectedAccessListUser?.access_list;
    const toIds = (arr: any[]) =>
      Array.isArray(arr) ? arr.map((item: any) => item?.id ?? item) : [];
    return {
      job_classification: toIds(access_list?.job_classifications),
      department: toIds(access_list?.departments),
      zone: toIds(access_list?.zones),
      location: toIds(access_list?.locations),
    };
  }, [selectedAccessListUser]);

  // Committed zone holds IDs (numbers), used to filter locations query
  const [committedZone, setCommittedZone] = useState<any>(
    formInitialValues.zone,
  );

  // Sync form fields each time the drawer opens or selected user changes
  useEffect(() => {
    setPage(1);
    if (isVisible) {
      form.setFieldsValue(formInitialValues);
      setCommittedZone(formInitialValues.zone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, selectedAccessListUser]);

  const hasValue = (val: any) => (Array.isArray(val) ? val?.length > 0 : !!val);

  // Job Classification — always fetched
  const { data: jobClassification } = useGetJobClassificationsQuery(
    {},
    { skip: !isVisible },
  );

  // Department — always fetched
  const { data: departments, isFetching: isDepartmentsFetching } =
    useGetDepartmentsQuery({ page_size: 100 }, { skip: !isVisible });

  // Zone — always fetched
  const { data: zones, isFetching: isZonesFetching } = useGetZonesQuery(
    { page_size: 100 },
    { skip: !isVisible },
  );

  // Location — filtered by committed zone, skipped until zone is committed
  const { data: locations, isFetching: isLocationsFetching } =
    useGetLocationsQuery(
      { page_size: 100, zone: committedZone?.join(",") },
      { skip: !hasValue(committedZone) },
    );
  const [page, setPage] = useState(1);
  const {
    data: usersData,
    isFetching: isUsersFetching,
    isError: isUsersError,
    refetch: refetchUsers,
  } = useGetPreviewUsersInfiniteQuery(
    {
      page,
      is_superuser: false,
      job_classification: jobClassificationValue?.join(","),
      department: departmentValue?.join(","),
      zone: zoneValue?.join(","),
      location: locationValue?.join(","),
    },
    { skip: !filtersApplied },
  );
  const users = usersData?.results ?? [];
  const isFirstPageLoading = isUsersFetching && page === 1;

  // Next page after the last loaded one, so repeated triggers can't duplicate requests
  const loadNextPage = useCallback(() => {
    if (usersData) setPage(usersData.page + 1);
  }, [usersData]);

  const sentinelRef = useLoadMoreOnScroll({
    hasNextPage: Boolean(usersData?.next),
    isFetching: isUsersFetching,
    isError: isUsersError,
    onLoadMore: loadNextPage,
  });

  const departmentsOptions = transformToOptions(departments);
  const zonesOptions = transformToOptions(zones);
  const locationsOptions = transformToGroupedOptions(locations, "zone");

  // Disable Apply Filters when: no fields filled, or zone filled without location
  const hasAnyField =
    hasValue(jobClassificationValue) ||
    hasValue(departmentValue) ||
    hasValue(zoneValue) ||
    hasValue(locationValue);

  const disabledApplyFilters = hasValue(zoneValue) && !hasValue(locationValue);

  const handleSelectAll = () => {
    // Mock functionality for select all filters
  };

  const handleSaveAccessList = () => {
    postAccessList({
      cfm_staff: selectedAccessListUser?.id,
      departments: departmentValue,
      zones: zoneValue,
      locations: locationValue,
      job_classifications: jobClassificationValue,
    })
      .unwrap()
      .then(() => {
        setIsVisible(false);
        showSuccessMsg({ msg: "Access List updated successfully" });
      })
      .catch(HandleErrors);
  };

  const handleApplyFilters = (data: any) => {
    console.log(data);
    setPage(1);
    setFiltersApplied(true);
  };

  // Reset location when zone changes
  const handleValuesChange = (changedValues: any) => {
    setFiltersApplied(false);
    setPage(1);
    if ("zone" in changedValues) {
      form.setFieldValue("location", undefined);
      const newZone = changedValues["zone"];
      if (!hasValue(newZone)) {
        setCommittedZone(undefined);
      }
    }
  };

  return (
    <Drawer
      title={
        <div className={styles.drawerHeader}>
          <Text className={styles.title}>Configure Access List</Text>
          <div className={styles.subtitle}>
            <Text>User:</Text>{" "}
            <Text className={styles.boldName}>
              {selectedAccessListUser?.user?.first_name}{" "}
              {selectedAccessListUser?.user?.last_name}
            </Text>
            <span className={styles.roleBadge}>
              {selectedAccessListUser?.user?.group?.name}
            </span>
          </div>
        </div>
      }
      headerStyle={{ borderBottom: "1px #D9D9D9 solid", paddingBlock: "24px" }}
      placement="right"
      onClose={() => setIsVisible(false)}
      open={isVisible}
      closeIcon={false}
      extra={
        <div onClick={() => setIsVisible(false)} className={styles.closeIcon}>
          <IoMdClose size={28} color="dark" />
        </div>
      }
      width={"40%"}
      className={`${styles.drawer} ${containerStyle}`}
    >
      <div className={styles.drawerContent}>
        <InfoCard
          variant="warning"
          title="Filter Intersection Logic"
          description='Employees must match ALL selected filters to be visible. For example, selecting "Full-Time" + "Operations" will show only full-time employees in the Operations department.'
        />
        <Form
          form={form}
          onFinish={handleApplyFilters}
          onValuesChange={handleValuesChange}
          initialValues={formInitialValues}
        >
          <div className={styles.filtersSection}>
            <div className={styles.filtersHeader}>
              <div className={styles.filtersText}>
                <Text className={styles.filtersTitle}>
                  Employee Visibility Filters
                </Text>
                <Text className={styles.filtersSubtitle}>
                  All employees within these filters will be visible to the
                  user.
                </Text>
              </div>
              {/* <span className={styles.selectAll} onClick={handleSelectAll}>
                                Select All
                            </span> */}
            </div>

            <div className={styles.filtersGrid}>
              <Form.Item name="job_classification">
                <SelectionInput
                  placeholder="Job Classification"
                  options={jobClassification || []}
                  isScrollable
                  mode="multiple"
                />
              </Form.Item>
              <Form.Item name="department">
                <SelectionInput
                  placeholder="Department"
                  options={departmentsOptions}
                  isLoading={isDepartmentsFetching}
                  isScrollable
                  mode="multiple"
                />
              </Form.Item>
              <Form.Item name="zone">
                <SelectionInput
                  placeholder="Zone"
                  options={zonesOptions}
                  isLoading={isZonesFetching}
                  isScrollable
                  mode="multiple"
                  onBlur={() => setCommittedZone(form.getFieldValue("zone"))}
                />
              </Form.Item>
              <Form.Item name="location">
                <SelectionInput
                  placeholder="Location"
                  options={locationsOptions}
                  disabled={!hasValue(zoneValue)}
                  isLoading={isLocationsFetching}
                  isScrollable
                  mode="multiple"
                />
              </Form.Item>
              <Button
                type="submit"
                btnClassName={styles.applyBtn}
                disabled={disabledApplyFilters}
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </Form>

        {filtersApplied && isFirstPageLoading ? (
          <Spin className="m-auto" />
        ) : filtersApplied && isUsersError && users.length === 0 ? (
          <div className={styles.errorState}>
            <Text>Couldn&apos;t load users.</Text>
            <Button isOutline onClick={() => refetchUsers()}>
              Retry
            </Button>
          </div>
        ) : filtersApplied && users.length > 0 && hasAnyField ? (
          <div className={styles.usersListSection}>
            <div className={styles.usersListHeader}>
              <Text className={styles.usersListTitle}>Access Preview</Text>
              <Text className={styles.usersListSubtitle}>
                {usersData?.count ?? users.length} Users
              </Text>
            </div>

            <div className={styles.usersList}>
              {users.map((item: any) => (
                <div key={item.id} className={styles.userRow}>
                  <UserInfo data={{ user: item, ...item }} isTableCol />
                </div>
              ))}
              {isUsersFetching && <Spin className="m-auto" />}
              {isUsersError && !isUsersFetching && (
                <div className={styles.errorState}>
                  <Text>Couldn&apos;t load more users.</Text>
                  <Button isOutline onClick={() => refetchUsers()}>
                    Retry
                  </Button>
                </div>
              )}
              <div ref={sentinelRef} aria-hidden />
            </div>
          </div>
        ) : filtersApplied && users.length === 0 ? (
          <Empty
            description="No users found"
            image={Empty.PRESENTED_IMAGE_DEFAULT}
          />
        ) : (
          <Empty
            description="Please apply filters to see users"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </div>

      <div className={styles.footer}>
        <Button
          btnClassName={styles.cancelBtn}
          isOutline
          onClick={() => setIsVisible(false)}
        >
          Cancel
        </Button>
        <Button
          btnClassName={styles.saveBtn}
          onClick={handleSaveAccessList}
          disabled={!filtersApplied || isFirstPageLoading}
          isLoading={isPostAccessListLoading || isFirstPageLoading}
        >
          Save Access List
        </Button>
      </div>
    </Drawer>
  );
}
